import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard } from "./functions";
import Loading from "../atoms/loading";
import Button from "../atoms/button";
import { buildProductsLink, deadlineWindowFilter } from "../filters/deadlineFilter";
import { statusObject } from "../utils/allProductStatus";
import {
    ACTIVE_QUEUE_STATUSES,
    INCOMING_STATUSES,
    ANALYZING_STATUSES,
    getUrgencyMeta,
    formatDate,
    describeDaysLeft,
    formatItems,
    artPendingSummary,
} from "./dashboardView";
import { isExtendedArt, orderPath } from "../extendedArt/kind";

const DEADLINE_SORT = "calculatedDeadline";

const SummaryCard = ({ title, bucket, accent, onClick }) => {
    const { items, ordersLabel } = formatItems(bucket);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition hover:border-primary ${accent ?? "border-primary/30 bg-mgs-surface"}`}
        >
            <span className="text-sm text-laranjinha/70">{title}</span>
            <span className="text-3xl font-bold text-laranjinha">{items}</span>
            <span className="text-xs text-laranjinha/60">itens · {ordersLabel}</span>
        </button>
    );
};

const UrgencyBadge = ({ urgency }) => {
    const meta = getUrgencyMeta(urgency);
    return (
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${meta.classes}`}>
            {meta.label}
        </span>
    );
};

const KindBadge = ({ entry }) => (
    isExtendedArt(entry)
        ? <span className="ml-2 rounded-full bg-purple-700 px-2 py-0.5 text-xs font-semibold text-white">Arte estendida</span>
        : null
);

const ArtPendingCard = ({ pending, onClick }) => {
    const summary = artPendingSummary(pending);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition hover:border-primary ${summary.late ? "border-red-600 bg-red-950/40" : "border-primary/30 bg-mgs-surface"}`}
        >
            <span className="text-sm text-laranjinha/70">Artes pendentes</span>
            <span className="text-3xl font-bold text-laranjinha">{summary.arts}</span>
            <span className="text-xs text-laranjinha/60">{`artes · ${summary.ordersLabel}${summary.lateLabel ? ` · ${summary.lateLabel}` : ""}`}</span>
        </button>
    );
};

const QueueRow = ({ entry, onOpen, highlight }) => (
    <button
        type="button"
        onClick={() => onOpen(entry)}
        className={`grid w-full grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 rounded-lg border px-3 py-2 text-left transition hover:border-primary ${highlight ? "border-primary bg-mgs-elevated" : "border-primary/20 bg-mgs-surface"}`}
    >
        <UrgencyBadge urgency={entry.urgency} />
        <div className="min-w-0">
            <p className="truncate font-medium text-laranjinha">{entry.userName ?? "Cliente sem nome"}<KindBadge entry={entry} /></p>
            <p className="truncate text-xs text-laranjinha/60">
                {statusObject[entry.status] ?? entry.status} · {describeDaysLeft(entry.daysLeft)}
            </p>
        </div>
        <span className="text-sm text-laranjinha/80">{entry.numberOfCards} itens</span>
        <span className="text-sm text-laranjinha/60">{formatDate(entry.dueDate)}</span>
        <span className="text-sm font-semibold text-primary">Abrir →</span>
    </button>
);

const Home = ({ user }) => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [clientSearch, setClientSearch] = useState("");

    const loadDashboard = async () => {
        setLoading(true);
        setError(null);
        const response = await getDashboard(user.token);
        if (response?.type === "success") {
            setData(response.data);
        } else {
            setError(response?.message ?? "Não foi possível carregar o painel.");
        }
        setLoading(false);
    };

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line
    }, []);

    const goToClientSearch = () => {
        const term = clientSearch.trim();
        const preset = term.includes("@") ? { presetEmail: term } : { presetName: term };
        navigate("/manafix/users", { state: preset });
    };

    const goToQueue = () => {
        navigate(`/manafix/products?status=${ACTIVE_QUEUE_STATUSES.join(",")}`);
    };

    const goToIncoming = () => {
        navigate(`/manafix/products?status=${INCOMING_STATUSES.join(",")}`);
    };

    const goToSlice = ({ statuses, window, sort }) => {
        navigate(buildProductsLink({ statuses, deadline: window ? deadlineWindowFilter(window) : undefined, sort }));
    };

    const goToCreateOrder = () => {
        navigate("/manafix/products/create", { state: null });
    };

    const openProduct = (entry) => {
        navigate(orderPath(entry));
    };

    const goToArtQueue = () => {
        navigate("/manafix/art-labels", { state: { tab: "queue" } });
    };

    const goToAnalyzing = () => {
        navigate(`/manafix/products?status=${ANALYZING_STATUSES.join(",")}`);
    };

    const queue = data?.queue;
    const priorityQueue = data?.priorityQueue ?? [];
    const nextOrder = priorityQueue[0];
    const incoming = data?.incoming;
    const analyzing = data?.analyzing;

    return (
        <main className="mx-auto w-full max-w-6xl p-4 text-laranjinha">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h1 className="text-left text-2xl font-bold">Central de operação</h1>
                    <p className="text-sm text-laranjinha/60">O que precisa da sua atenção agora</p>
                </div>
                <Button click={loadDashboard} classes="bg-mgs-elevated text-laranjinha border border-primary/40 hover:border-primary">
                    {loading ? "Atualizando…" : "Atualizar"}
                </Button>
            </div>

            <section aria-label="Ações rápidas" className="mb-6 flex flex-wrap items-stretch gap-3">
                <form
                    className="flex min-w-[260px] flex-1 items-center gap-2 rounded-xl border border-primary/30 bg-mgs-surface p-2"
                    onSubmit={(e) => { e.preventDefault(); goToClientSearch(); }}
                >
                    <input
                        className="w-full bg-transparent px-2 py-1 text-laranjinha placeholder-laranjinha/40 outline-none"
                        type="text"
                        placeholder="Buscar cliente por nome ou e-mail…"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                    />
                    <Button type="submit" classes="bg-[#d59106] hover:bg-[#b47c06] whitespace-nowrap">Buscar cliente</Button>
                </form>
                <Button click={goToQueue} classes="bg-mgs-surface text-laranjinha border border-primary/30 hover:border-primary px-6">
                    Fila de pedidos
                </Button>
                <Button click={goToIncoming} classes="bg-mgs-surface text-laranjinha border border-primary/30 hover:border-primary px-6">
                    Pedidos a receber
                </Button>
                <Button click={goToCreateOrder} classes="bg-mgs-surface text-laranjinha border border-primary/30 hover:border-primary px-6">
                    + Novo pedido
                </Button>
            </section>

            {loading && !data ? <Loading classes="m-auto" /> : null}
            {error ? (
                <div className="m-8 text-center">
                    <p className="text-red-500">{error}</p>
                    <Button click={loadDashboard} classes="mt-2 bg-[#d59106] hover:bg-[#b47c06]">Tentar de novo</Button>
                </div>
            ) : null}

            {data ? (
                <>
                    <section aria-label="Resumo da operação" className="mb-6">
                        <h2 className="mb-2 text-left text-lg font-semibold">Resumo da operação</h2>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                            <SummaryCard
                                title="Atrasados"
                                bucket={queue?.overdue}
                                accent={queue?.overdue?.orders ? "border-red-600 bg-red-950/40" : "border-primary/30 bg-mgs-surface"}
                                onClick={() => goToSlice({ statuses: ACTIVE_QUEUE_STATUSES, window: "overdue", sort: DEADLINE_SORT })}
                            />
                            <SummaryCard title="Próx. 7 dias" bucket={queue?.dueIn7} onClick={() => goToSlice({ statuses: ACTIVE_QUEUE_STATUSES, window: 7, sort: DEADLINE_SORT })} />
                            <SummaryCard title="Próx. 15 dias" bucket={queue?.dueIn15} onClick={() => goToSlice({ statuses: ACTIVE_QUEUE_STATUSES, window: 15, sort: DEADLINE_SORT })} />
                            <SummaryCard title="Próx. 30 dias" bucket={queue?.dueIn30} onClick={() => goToSlice({ statuses: ACTIVE_QUEUE_STATUSES, window: 30, sort: DEADLINE_SORT })} />
                            <SummaryCard title="Total na fila" bucket={queue?.total} onClick={() => goToSlice({ statuses: ACTIVE_QUEUE_STATUSES })} />
                            {data.artLabelsPending ? (
                                <ArtPendingCard pending={data.artLabelsPending} onClick={goToArtQueue} />
                            ) : null}
                        </div>
                        {queue?.noDeadline?.orders ? (
                            <p className="mt-2 text-left text-xs text-laranjinha/50">
                                {queue.noDeadline.orders} pedido(s) na fila ainda sem prazo definido ({queue.noDeadline.items} itens).
                            </p>
                        ) : null}
                    </section>

                    <section aria-label="Próximo pedido" className="mb-6">
                        <h2 className="mb-2 text-left text-lg font-semibold">Próximo pedido para trabalhar</h2>
                        {nextOrder ? (
                            <div className="rounded-xl border border-primary bg-mgs-elevated p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="text-left">
                                        <div className="mb-1 flex items-center gap-2">
                                            <UrgencyBadge urgency={nextOrder.urgency} /><KindBadge entry={nextOrder} />
                                            <span className="text-sm text-laranjinha/70">
                                                {statusObject[nextOrder.status] ?? nextOrder.status}
                                            </span>
                                        </div>
                                        <p className="text-xl font-bold">{nextOrder.userName ?? "Cliente sem nome"}</p>
                                        <p className="text-sm text-laranjinha/70">
                                            {nextOrder.numberOfCards} itens · prazo {formatDate(nextOrder.dueDate)} · {describeDaysLeft(nextOrder.daysLeft)}
                                            {nextOrder.workDaysNeeded > 1 ? ` · ~${nextOrder.workDaysNeeded} dias de trabalho` : ""}
                                        </p>
                                    </div>
                                    <Button click={() => openProduct(nextOrder)} classes="bg-[#d59106] hover:bg-[#b47c06] px-6">
                                        Abrir pedido
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <p className="rounded-xl border border-primary/20 bg-mgs-surface p-4 text-left text-laranjinha/60">
                                Nenhum pedido na fila de produção. 🎉
                            </p>
                        )}
                    </section>

                    {priorityQueue.length > 1 ? (
                        <section aria-label="Fila priorizada" className="mb-6">
                            <div className="mb-2 flex items-center justify-between">
                                <h2 className="text-left text-lg font-semibold">Fila priorizada</h2>
                                <button type="button" onClick={goToQueue} className="text-sm text-primary hover:underline">
                                    Ver fila completa →
                                </button>
                            </div>
                            <div className="flex flex-col gap-2">
                                {priorityQueue.slice(1).map((entry) => (
                                    <QueueRow key={entry.id} entry={entry} onOpen={openProduct} />
                                ))}
                            </div>
                        </section>
                    ) : null}

                    <section aria-label="A caminho" className="mb-6">
                        <h2 className="mb-2 text-left text-lg font-semibold">A caminho da Manafix</h2>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            {INCOMING_STATUSES.map((status) => {
                                const { items, ordersLabel } = formatItems(incoming?.byStatus?.[status]);
                                return (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => goToSlice({ statuses: [status] })}
                                        className="rounded-xl border border-primary/20 bg-mgs-surface p-3 text-left transition hover:border-primary"
                                    >
                                        <p className="text-sm text-laranjinha/70">{statusObject[status]}</p>
                                        <p className="text-lg font-bold">{items} <span className="text-xs font-normal text-laranjinha/60">itens · {ordersLabel}</span></p>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section aria-label="Em análise" className="mb-6">
                        <div className="mb-2 flex items-center justify-between">
                            <h2 className="text-left text-base font-semibold text-laranjinha/70">
                                Em análise
                                <span className="ml-2 text-xs font-normal text-laranjinha/50">
                                    {analyzing?.total?.items ?? 0} itens · {formatItems(analyzing?.total).ordersLabel} — fora da fila de produção
                                </span>
                            </h2>
                            <button type="button" onClick={goToAnalyzing} className="text-sm text-laranjinha/60 hover:text-primary hover:underline">
                                Ver todos →
                            </button>
                        </div>
                        {analyzing?.total?.orders ? (
                            <div className="flex flex-col gap-1">
                                {(analyzing.entries ?? []).map((entry) => (
                                    <button
                                        key={entry.id}
                                        type="button"
                                        onClick={() => openProduct(entry)}
                                        className="flex items-center justify-between gap-3 rounded-lg border border-primary/10 bg-mgs-surface/60 px-3 py-2 text-left transition hover:border-primary/40"
                                    >
                                        <span className="truncate text-sm text-laranjinha/70">{entry.userName ?? "Cliente sem nome"}<KindBadge entry={entry} /></span>
                                        <span className="whitespace-nowrap text-xs text-laranjinha/50">
                                            {entry.numberOfCards} itens · {describeDaysLeft(entry.daysLeft)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="rounded-lg border border-primary/10 bg-mgs-surface/60 px-3 py-2 text-left text-sm text-laranjinha/50">
                                Nenhum pedido em análise.
                            </p>
                        )}
                    </section>

                    <footer className="border-t border-primary/20 pt-3 text-left text-xs text-laranjinha/50">
                        {data?.totals?.users ?? "—"} usuários registrados · {data?.totals?.serials ?? "—"} seriais cadastrados
                    </footer>
                </>
            ) : null}
        </main>
    );
};

export default Home;
